import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { FuelCardService } from '../../../fuel-card/services/fuel-card.service';
import { catchError, exhaustMap, finalize, first, map, switchMap, tap } from 'rxjs/operators';
import { EMPTY, forkJoin, of } from 'rxjs';
import * as FuelCardActions from '../actions/fuel-card.actions';
import { ErrorHandlerService } from '../../../fuel-card/services/error-handler.service';
import { FuelingMerge } from '../../../fuel-card/models/fueling-merge';
import { UpdateFuelingResponse } from '../../../fuel-card/interfaces/update-fueling-response';
import { FuelCardFacade } from '../facade/fuel-card.facade';
import { deleteFuelingMergeById, mergeFuelingMerge } from '../actions/fuel-card.actions';
import { LoadingBarState } from '@ngx-loading-bar/core/loading-bar.state';
import { LoadingBarService } from '@ngx-loading-bar/core';

@Injectable()
export class FuelCardsEffects {

  getFuelCards$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.getFuelCards.type),
      switchMap((action: any) => this._fuelCardService.getFuelCards(action.currentPage, action.pageSize)
        .pipe(
          exhaustMap((data) => of(FuelCardActions.getFuelCardsSuccess({data: data}))),
          catchError(error => of(FuelCardActions.getFuelCardsFailure({error})))
        )
      )
    )
  );

  getFilesMetaData$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.getFilesMetaData.type),
      switchMap((action: any) => this._fuelCardService.getFilesMetaData()
        .pipe(
          exhaustMap((data) => of(FuelCardActions.getFilesMetaDataSuccess({data: data}))),
          catchError(error => of(FuelCardActions.getFilesMetaDataFailure({error})))
        )
      )
    )
  );

  parseFuelCardFile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.parseFuelCardFile.type),
      switchMap((action: any) => this._fuelCardService.parseFuelCard(action.form)
        .pipe(
          exhaustMap((data) => of(FuelCardActions.parseFuelCardFileSuccess({data: data}))),
          catchError(error => of(FuelCardActions.parseFuelCardFileFailure({error})))
        )
      )
    )
  );

  saveParsedFuelCardFile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.saveParsedFuelCardFile.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._fuelCardService.saveParsedFuelCard(action.data)
        .pipe(
          exhaustMap((data) => of(FuelCardActions.saveParsedFuelCardFileSuccess({data: data}))),
          catchError(error => of(FuelCardActions.saveParsedFuelCardFileFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );
  getFuelingMergeData$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.getFuelingMergeData.type),
      tap(() => this._loader.start()),
      switchMap((action: any) =>
        this._fuelCardService.getFuelingMerge(action.unitIds, action.from, action.to).pipe(
          map((data) => FuelCardActions.getFuelingMergeDataSuccess({ data })),
          catchError((error) => {
            this._errorHandlerService.handleError(error);
            return of(FuelCardActions.getFuelingMergeDataFailure({ error }));
          }),
          tap(() => this._loader.complete())
        )
      )
    )
  );

  updateFuelingMerge$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.updateFuelingMerge.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._fuelCardService.updateFuelingMerge(action.fuelingMergeId, action.data)
        .pipe(
          exhaustMap((data) => of(FuelCardActions.updateFuelingMergeSuccess({data: data}))),
          tap(res => {
            const ids = [res?.data?.fuelingMergeId];
            if (res?.data?.orphantNewParentFuelingMergeIds) {
              ids.push(...res.data.orphantNewParentFuelingMergeIds);
            }
            this._fuelCardFacade.getFuelingMergeByIds(ids);
          }),
          catchError(error => {
            this._errorHandlerService.handleError(error);
            return of(FuelCardActions.updateFuelingMergeFailure({error}));
          }),
          tap(() => this._loader.complete()),
        )
      )
    )
  );


  approveFuelingMergeByIds$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.approveFuelingMergeByIds.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => {
        const subs = [];
        action.fuelingMergeIds.forEach((id: number) => subs.push(this._fuelCardService.approveFuelingMerge(id)));
        return forkJoin(subs).pipe(
          exhaustMap((res: any[]) => of(FuelCardActions.approveFuelingMergeByIdsSuccess({data: res}))),
          // tap(res => this._fuelCardFacade.getFuelingMergeByIds(res?.data)),
          catchError(error => of(FuelCardActions.approveFuelingMergeByIdsFailure(error))),
          tap(() => this._loader.complete()),
        );
      })
    )
  );

  markAsRealFuelingByIds$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.markAsRealFuelingByIds),
      switchMap((action: any) => {
        const requests$ = action.fuelingMergeIds.map((id: number) => this._fuelCardService.markAsRealFueling(id));
        return forkJoin(requests$).pipe(
          map(() => FuelCardActions.markAsRealFuelingByIdsSuccess({ data: action.fuelingMergeIds })),
          catchError(error => of(FuelCardActions.markAsRealFuelingByIdsFailure({ error }))),
          finalize(() => this._loader.complete())
        );
      })
    )
  );

  markAsNotRealFuelingByIds$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.markAsNotRealFuelingByIds),
      switchMap((action: any) => {
        const requests$ = action.fuelingMergeIds.map((id: number) => this._fuelCardService.markAsNotRealFueling(id));
        return forkJoin(requests$).pipe(
          map(() => FuelCardActions.markAsNotRealFuelingByIdsSuccess({ data: action.fuelingMergeIds })),
          catchError(error => of(FuelCardActions.markAsNotRealFuelingByIdsFailure({ error }))),
          finalize(() => this._loader.complete())
        );
      })
    )
  );

  approveFuelingMergeByIdsSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.approveFuelingMergeByIdsSuccess.type),
      switchMap((action: any) => {
        return of(FuelCardActions.getFuelingMergeByIds({fuelingMergeIds: action?.data}));
      })
    )
  );
/////////////////
  getFuelingMergeByIds$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.getFuelingMergeByIds.type),
      switchMap((action: any) => {
        const subs = [];
        action.fuelingMergeIds.forEach((id: number) => subs.push(this._fuelCardService.getFuelingMergeById(id).pipe(
          first()
        )));

        return forkJoin(subs).pipe(
          exhaustMap((res: FuelingMerge[]) => of(FuelCardActions.getFuelingMergeByIdsSuccess({data: res}))),
          catchError(error => of(FuelCardActions.getFuelingMergeByIdsFailure(error))),
          tap(() => this._loader.complete()),
        );
      })
    )
  );

  mergeFuelingMerge$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.mergeFuelingMerge.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._fuelCardService.mergeFuelingMerge(action.data, action.id1, action.id2)
        .pipe(
          exhaustMap((data) => of(FuelCardActions.mergeFuelingMergeSuccess({data: data}))),
          tap(res => {
            const ids = [res?.data?.fuelingMergeId];
            if (res?.data?.orphantNewParentFuelingMergeIds) {
              ids.push(...res.data.orphantNewParentFuelingMergeIds);
            }
            this._fuelCardFacade.getFuelingMergeByIds(ids);
          }),
          catchError(error => {
            this._errorHandlerService.handleError(error);
            return of(FuelCardActions.mergeFuelingMergeFailure({error}));
          }),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  unMergeFuelingMerge$ = createEffect(() =>
  this._actions$.pipe(
    ofType(FuelCardActions.unMergeFuelingMerge),

    tap(() => this._loader.start()),

    switchMap((action) =>
      this._fuelCardService.unMergeFuelingMerge(
        action.fuelingMergeId,
        action.data
      ).pipe(

        map((eventIds: number[]) =>
          FuelCardActions.unMergeFuelingMergeSuccess({
            fuelingMergeId: action.fuelingMergeId,
            data: {
              fuelingMergeId: action.fuelingMergeId,
              deletedFuelingMergeId1: eventIds?.[0],
              deletedFuelingMergeId2: eventIds?.[1],
              orphantNewParentFuelingMergeIds: []
            }
          })
        ),

        tap((actionSuccess) => {
          const ids: number[] = [];

          const data = actionSuccess.data;

          if (data.deletedFuelingMergeId1) {
            ids.push(data.deletedFuelingMergeId1);
          }

          if (data.deletedFuelingMergeId2) {
            ids.push(data.deletedFuelingMergeId2);
          }

          if (ids.length > 0) {
            this._fuelCardFacade.getFuelingMergeByIds(ids);
          }
        }),

        catchError(error => {
          this._errorHandlerService.handleError(error);
          return of(
            FuelCardActions.unMergeFuelingMergeFailure({ error })
          );
        }),

        tap(() => this._loader.complete())
      )
    )
  )
);
  deleteFuelingMergeById$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.deleteFuelingMergeById.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._fuelCardService.deleteFuelingMergeById(action.fuelingMergeId)
        .pipe(
          exhaustMap((data) => of(FuelCardActions.deleteFuelingMergeByIdSuccess({fuelingMergeId: action.fuelingMergeId}))),
          catchError(error => {
            this._errorHandlerService.handleError(error);
            return of(FuelCardActions.deleteFuelingMergeByIdFailure({error}));
          }),
          tap(() => this._loader.complete()),
        )
      )
    )
  );
  addFuelingMerge$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FuelCardActions.addFuelingMerge.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._fuelCardService.addFuelingMerge(action.data)
        .pipe(
          exhaustMap((data) => of(FuelCardActions.addFuelingMergeSuccess({data}))),
          tap(res => {
            const ids = [res?.data?.fuelingMergeId];
            if (ids.length > 0) {
              this._fuelCardFacade.getFuelingMergeByIds(ids);
            }
          }),
          catchError(error => {
            this._errorHandlerService.handleError(error);
            return of(FuelCardActions.addFuelingMergeFailure({error}));
          }),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  private _loader: LoadingBarState;

  constructor(
    private _actions$: Actions,
    private _fuelCardService: FuelCardService,
    private _errorHandlerService: ErrorHandlerService,
    private _fuelCardFacade: FuelCardFacade,
    private _loadingBar: LoadingBarService
  ) {
    this._loader = this._loadingBar.useRef('main');
  }
}
